import { buildShareMicrocopy, extractShareDate, formatShareDate, SHARE_APP_HOST } from "./lib/share-card";

type ShareSnapshot = {
  headline: string;
  photoUrl: string;
  microcopy: string;
  dateText: string;
};

const SHOW_TIME_KEY = "sorakan-share-show-time";
const SHOW_APP_KEY = "sorakan-share-show-app";
let lastEasySummary = "";
let currentSnapshot: ShareSnapshot | null = null;
let showTime = readPreference(SHOW_TIME_KEY, true);
let showApp = readPreference(SHOW_APP_KEY, true);

function readPreference(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return fallback;
    return value === "1";
  } catch {
    return fallback;
  }
}

function writePreference(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // Sharing still works when storage is unavailable.
  }
}

function installStyles(): void {
  const style = document.createElement("style");
  style.textContent = `
    .share-sky-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      min-height: 48px;
      margin: 14px 0 18px;
      border: 1px solid rgba(25,133,165,.28);
      border-radius: 16px;
      background: rgba(255,255,255,.78);
      color: #197f9c;
      font: inherit;
      font-size: .88rem;
      font-weight: 800;
      box-shadow: 0 8px 24px rgba(32,81,94,.06);
      cursor: pointer;
    }
    .share-sky-button svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
    .share-overlay {
      position: fixed;
      z-index: 300;
      inset: 0;
      display: grid;
      place-items: center;
      padding: 18px;
      background: rgba(12,35,42,.48);
      backdrop-filter: blur(5px);
    }
    .share-sheet {
      width: min(100%, 500px);
      max-height: min(88dvh, 760px);
      overflow-y: auto;
      border: 1px solid rgba(36,91,105,.14);
      border-radius: 26px;
      padding: 20px;
      background: #f8fcfc;
      box-shadow: 0 28px 90px rgba(8,34,42,.32);
    }
    .share-sheet__header { display: flex; align-items: start; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
    .share-sheet__header span { display: block; margin-bottom: 3px; color: #1985a5; font-size: .68rem; font-weight: 800; letter-spacing: .08em; }
    .share-sheet__header h2 { margin: 0; color: #183039; font-size: 1.28rem; }
    .share-sheet__close { width: 38px; height: 38px; flex: 0 0 38px; border: 0; border-radius: 50%; background: #e5f0f2; color: #456872; font-size: 1.4rem; cursor: pointer; }
    .share-card-preview { overflow: hidden; border: 1px solid rgba(36,91,105,.12); border-radius: 22px; background: white; box-shadow: 0 14px 34px rgba(32,81,94,.08); }
    .share-card-preview__copy { padding: 17px 17px 15px; }
    .share-card-preview h3 { margin: 0 0 12px; color: #17343e; font-size: 1.08rem; line-height: 1.45; }
    .share-card-preview img { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
    .share-card-preview__text { margin: 0; color: #365b66; font-size: .82rem; line-height: 1.75; white-space: pre-line; }
    .share-card-preview__meta { display: flex; justify-content: space-between; gap: 10px; margin-top: 14px; padding-top: 11px; border-top: 1px solid #e6eff1; color: #789097; font-size: .68rem; font-weight: 700; }
    .share-options { display: grid; gap: 9px; margin: 16px 0 10px; }
    .share-option { display: flex; align-items: center; justify-content: space-between; gap: 16px; min-height: 50px; padding: 10px 13px; border-radius: 15px; background: #edf6f7; color: #315a65; font-size: .82rem; font-weight: 800; }
    .share-option input { width: 22px; height: 22px; accent-color: #1985a5; }
    .share-privacy { margin: 10px 2px 14px; color: #6c838a; font-size: .70rem; line-height: 1.6; }
    .share-actions { display: grid; grid-template-columns: .75fr 1.4fr; gap: 9px; }
    .share-actions button { min-height: 48px; border: 0; border-radius: 15px; font: inherit; font-weight: 800; cursor: pointer; }
    .share-actions__cancel { background: #e3eef0; color: #466770; }
    .share-actions__primary { background: #1985a5; color: white; }
    .share-actions__primary:disabled { opacity: .55; cursor: wait; }
    .share-status { min-height: 1.2em; margin: 10px 2px 0; color: #54727b; font-size: .70rem; line-height: 1.5; }
    @media (max-width: 520px) {
      .share-overlay { align-items: end; padding: 10px; }
      .share-sheet { max-height: 91dvh; border-radius: 24px 24px 18px 18px; padding: 18px 16px 16px; }
    }
  `;
  document.head.appendChild(style);
}

function shareIcon(): string {
  return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4"></path><path d="m7.5 8.5 4.5-4.5 4.5 4.5"></path><path d="M5 12v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"></path></svg>';
}

function captureEasySummary(): void {
  const lead = document.querySelector<HTMLElement>(".page--result .lead-copy");
  if (lead?.textContent?.trim()) lastEasySummary = lead.textContent.trim();
}

function normalizeOfficialUrl(): void {
  const guide = document.querySelector<HTMLElement>(".pwa-guide-body");
  if (!guide) return;
  const official = guide.querySelector<HTMLElement>(".pwa-guide-url strong");
  if (official && official.textContent !== SHARE_APP_HOST) official.textContent = SHARE_APP_HOST;
  for (const bold of guide.querySelectorAll<HTMLElement>("b")) {
    if (bold.textContent?.trim() === "sorakan.pages.dev") bold.textContent = SHARE_APP_HOST;
  }
}

function enhanceResultView(): void {
  captureEasySummary();
  normalizeOfficialUrl();
  const page = document.querySelector<HTMLElement>(".page--result");
  const photo = page?.querySelector<HTMLElement>(".result-photo");
  if (!page || !photo || page.querySelector(".share-sky-button")) return;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "share-sky-button";
  button.dataset.shareAction = "open";
  button.innerHTML = `${shareIcon()}<span>この空を共有</span>`;
  photo.insertAdjacentElement("afterend", button);
}

function snapshotFromResult(): ShareSnapshot | null {
  const page = document.querySelector<HTMLElement>(".page--result");
  if (!page) return null;
  const headline = page.querySelector<HTMLElement>(".result-header h1")?.textContent?.trim() ?? "";
  const image = page.querySelector<HTMLImageElement>(".result-photo img");
  const headerMeta = page.querySelector<HTMLElement>(".result-header p")?.textContent ?? "";
  const easy = page.querySelector<HTMLElement>(".lead-copy")?.textContent?.trim() || lastEasySummary;
  const photoUrl = image?.currentSrc || image?.src || "";
  if (!headline || !photoUrl) return null;
  return {
    headline,
    photoUrl,
    microcopy: buildShareMicrocopy(easy),
    dateText: extractShareDate(headerMeta),
  };
}

function openShareDialog(): void {
  currentSnapshot = snapshotFromResult();
  if (!currentSnapshot) return;
  document.querySelector(".share-overlay")?.remove();

  const overlay = document.createElement("div");
  overlay.className = "share-overlay";
  overlay.innerHTML = `
    <section class="share-sheet" role="dialog" aria-modal="true" aria-labelledby="share-title">
      <header class="share-sheet__header">
        <div><span>空の共有カード</span><h2 id="share-title">この空を共有する</h2></div>
        <button class="share-sheet__close" type="button" data-share-action="close" aria-label="閉じる">×</button>
      </header>
      <article class="share-card-preview" aria-label="共有カードのプレビュー">
        <div class="share-card-preview__copy"><h3 data-share-preview="headline"></h3></div>
        <img data-share-preview="photo" alt="共有する空の写真">
        <div class="share-card-preview__copy">
          <p class="share-card-preview__text" data-share-preview="text"></p>
          <div class="share-card-preview__meta"><span data-share-preview="date"></span><span data-share-preview="app"></span></div>
        </div>
      </article>
      <div class="share-options">
        <label class="share-option"><span>撮影時間も表示</span><input type="checkbox" data-share-option="time" ${showTime ? "checked" : ""}></label>
        <label class="share-option"><span>${SHARE_APP_HOST} を表示</span><input type="checkbox" data-share-option="app" ${showApp ? "checked" : ""}></label>
      </div>
      <p class="share-privacy">場所名・緯度経度は共有カードに一切入れません。画像は新しく作り直すため、元写真の位置情報（EXIF）も引き継ぎません。</p>
      <div class="share-actions">
        <button class="share-actions__cancel" type="button" data-share-action="close">閉じる</button>
        <button class="share-actions__primary" type="button" data-share-action="share">共有する</button>
      </div>
      <p class="share-status" role="status"></p>
    </section>`;
  document.body.appendChild(overlay);
  document.documentElement.style.overflow = "hidden";
  updatePreview();
}

function closeShareDialog(): void {
  document.querySelector(".share-overlay")?.remove();
  document.documentElement.style.overflow = "";
  currentSnapshot = null;
}

function updatePreview(): void {
  const snapshot = currentSnapshot;
  const overlay = document.querySelector<HTMLElement>(".share-overlay");
  if (!snapshot || !overlay) return;
  const headline = overlay.querySelector<HTMLElement>('[data-share-preview="headline"]');
  const photo = overlay.querySelector<HTMLImageElement>('[data-share-preview="photo"]');
  const text = overlay.querySelector<HTMLElement>('[data-share-preview="text"]');
  const date = overlay.querySelector<HTMLElement>('[data-share-preview="date"]');
  const app = overlay.querySelector<HTMLElement>('[data-share-preview="app"]');
  if (headline) headline.textContent = snapshot.headline;
  if (photo) photo.src = snapshot.photoUrl;
  if (text) text.textContent = snapshot.microcopy;
  if (date) date.textContent = formatShareDate(snapshot.dateText, showTime);
  if (app) app.textContent = showApp ? SHARE_APP_HOST : "";
}

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("写真を共有カードに読み込めませんでした。"));
    image.src = src;
  });
}

function drawCoverImage(ctx: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, width: number, height: number): void {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const sourceWidth = width / scale;
  const sourceHeight = height / scale;
  const sourceX = (image.naturalWidth - sourceWidth) / 2;
  const sourceY = (image.naturalHeight - sourceHeight) / 2;
  ctx.save();
  roundedRectPath(ctx, x, y, width, height, 34);
  ctx.clip();
  ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
  ctx.restore();
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const characters = Array.from(text.replace(/\s+/g, " ").trim());
  const lines: string[] = [];
  let line = "";
  for (const character of characters) {
    const candidate = line + character;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line.trim());
      line = character;
      if (lines.length === maxLines) break;
    } else {
      line = candidate;
    }
  }
  if (lines.length < maxLines && line.trim()) lines.push(line.trim());
  if (lines.length === maxLines) {
    const consumed = lines.join("").length;
    if (consumed < characters.length) lines[maxLines - 1] = `${lines[maxLines - 1].replace(/[。！？、,.\s]*$/u, "")}…`;
  }
  return lines;
}

function drawLines(ctx: CanvasRenderingContext2D, lines: string[], x: number, y: number, lineHeight: number): number {
  lines.forEach((line, index) => ctx.fillText(line, x, y + index * lineHeight));
  return y + lines.length * lineHeight;
}

async function createShareCard(snapshot: ShareSnapshot): Promise<Blob> {
  const image = await loadImage(snapshot.photoUrl);
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("共有カードを作成できませんでした。");

  ctx.fillStyle = "#f4fafb";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = "#17343e";
  ctx.font = '700 58px "Hiragino Sans", "Yu Gothic", sans-serif';
  ctx.textBaseline = "top";
  const titleLines = wrapLines(ctx, snapshot.headline, 936, 2);
  drawLines(ctx, titleLines, 72, 70, 78);

  drawCoverImage(ctx, image, 56, 230, 968, 570);

  ctx.fillStyle = "#355d67";
  ctx.font = '500 38px "Hiragino Sans", "Yu Gothic", sans-serif';
  const bodyLines = wrapLines(ctx, snapshot.microcopy, 936, 4);
  drawLines(ctx, bodyLines, 72, 854, 60);

  ctx.strokeStyle = "#d8e7ea";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(72, 1224);
  ctx.lineTo(1008, 1224);
  ctx.stroke();

  ctx.fillStyle = "#718991";
  ctx.font = '600 27px "Hiragino Sans", "Yu Gothic", sans-serif';
  ctx.textBaseline = "alphabetic";
  const date = formatShareDate(snapshot.dateText, showTime);
  ctx.fillText(date, 72, 1288);
  if (showApp) {
    const width = ctx.measureText(SHARE_APP_HOST).width;
    ctx.fillText(SHARE_APP_HOST, 1008 - width, 1288);
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("共有カードを画像にできませんでした。"))), "image/png", 1);
  });
}

function fileName(snapshot: ShareSnapshot): string {
  const stamp = snapshot.dateText.replace(/[^0-9]/g, "").slice(0, 8);
  return `sorakan-${stamp || "sky"}.png`;
}

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function shareCurrentCard(): Promise<void> {
  const snapshot = currentSnapshot;
  const overlay = document.querySelector<HTMLElement>(".share-overlay");
  const button = overlay?.querySelector<HTMLButtonElement>('[data-share-action="share"]');
  const status = overlay?.querySelector<HTMLElement>(".share-status");
  if (!snapshot || !button) return;

  button.disabled = true;
  button.textContent = "カードを作成中…";
  if (status) status.textContent = "";
  try {
    const blob = await createShareCard(snapshot);
    const name = fileName(snapshot);
    const file = new File([blob], name, { type: "image/png" });
    const canShareFile = typeof navigator.share === "function" && (typeof navigator.canShare !== "function" || navigator.canShare({ files: [file] }));
    if (canShareFile) {
      try {
        await navigator.share({ files: [file], title: snapshot.headline });
        if (status) status.textContent = "共有メニューに渡しました。";
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    downloadBlob(blob, name);
    if (status) status.textContent = "共有メニューを使えないため、共有カード画像として保存しました。";
  } catch (error) {
    if (status) status.textContent = error instanceof Error ? error.message : "共有カードを作成できませんでした。";
  } finally {
    button.disabled = false;
    button.textContent = "共有する";
  }
}

installStyles();
document.addEventListener("click", (event) => {
  const element = event.target as HTMLElement;
  if (element.classList.contains("share-overlay")) {
    closeShareDialog();
    return;
  }
  const action = element.closest<HTMLElement>("[data-share-action]")?.dataset.shareAction;
  if (action === "open") openShareDialog();
  if (action === "close") closeShareDialog();
  if (action === "share") void shareCurrentCard();
});

document.addEventListener("change", (event) => {
  const input = (event.target as HTMLElement).closest<HTMLInputElement>("[data-share-option]");
  if (!input) return;
  if (input.dataset.shareOption === "time") {
    showTime = input.checked;
    writePreference(SHOW_TIME_KEY, showTime);
  }
  if (input.dataset.shareOption === "app") {
    showApp = input.checked;
    writePreference(SHOW_APP_KEY, showApp);
  }
  updatePreview();
});

const observer = new MutationObserver(() => enhanceResultView());
observer.observe(document.documentElement, { childList: true, subtree: true });
queueMicrotask(enhanceResultView);
